import { type ReactNode, useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.js";
import { NotificationBell } from "./NotificationBell.js";
import { UserMenu } from "./UserMenu.js";
import { CookieConsent } from "./CookieConsent.js";

/* ------------------------------------------------------------------ */
/*  Nav link definitions                                               */
/* ------------------------------------------------------------------ */

interface NavItem {
  label: string;
  to: string;
  authOnly?: boolean;
  /** SVG path(s) for the mobile bottom-bar icon (24x24 viewBox) */
  icon?: string;
}

const NAV_LINKS: NavItem[] = [
  {
    label: "Search",
    to: "/search",
    icon: "M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z",
  },
  {
    label: "Collection",
    to: "/collection",
    authOnly: true,
    icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
  },
  {
    label: "Marketplace",
    to: "/marketplace",
    icon: "M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z",
  },
  {
    label: "Wishlist",
    to: "/wishlist",
    authOnly: true,
    icon: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z",
  },
  {
    label: "Analytics",
    to: "/analytics",
    authOnly: true,
    icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
  },
  {
    label: "Achievements",
    to: "/achievements",
    authOnly: true,
    icon: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z",
  },
];

/** Mobile bottom-bar items (subset) */
const MOBILE_TABS: NavItem[] = [
  { label: "Search", to: "/search", icon: "M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" },
  { label: "Collection", to: "/collection", authOnly: true, icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" },
  { label: "Marketplace", to: "/marketplace", icon: "M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" },
  { label: "Profile", to: "/settings", authOnly: true, icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function isActive(pathname: string, to: string) {
  if (to === "/") return pathname === "/";
  return pathname.startsWith(to);
}

function NavIcon({ d }: { d: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Mobile sidebar overlay                                             */
/* ------------------------------------------------------------------ */

function MobileSidebar({
  open,
  onClose,
  isAuthenticated,
  pathname,
}: {
  open: boolean;
  onClose: () => void;
  isAuthenticated: boolean;
  pathname: string;
}) {
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Close on route change
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Trap focus & prevent body scroll
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 transition-opacity duration-300 md:hidden ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        ref={sidebarRef}
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-surface-card border-r border-white/10 transform transition-transform duration-300 ease-in-out md:hidden ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
          <Link
            to="/"
            className="text-lg font-bold text-brand-yellow"
            onClick={onClose}
          >
            PokeDex Collector
          </Link>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/10 transition-colors text-gray-400"
            aria-label="Close menu"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="flex flex-col gap-1 p-3">
          {NAV_LINKS.filter((l) => !l.authOnly || isAuthenticated).map(
            (link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={onClose}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(pathname, link.to)
                    ? "bg-brand-yellow/10 text-brand-yellow"
                    : "text-gray-300 hover:bg-white/5 hover:text-gray-100"
                }`}
              >
                {link.icon && <NavIcon d={link.icon} />}
                {link.label}
              </Link>
            ),
          )}

          {isAuthenticated && (
            <>
              <div className="border-t border-white/10 my-2" />
              <Link
                to="/proposals"
                onClick={onClose}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(pathname, "/proposals")
                    ? "bg-brand-yellow/10 text-brand-yellow"
                    : "text-gray-300 hover:bg-white/5 hover:text-gray-100"
                }`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Proposals
              </Link>
              <Link
                to="/settings"
                onClick={onClose}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(pathname, "/settings")
                    ? "bg-brand-yellow/10 text-brand-yellow"
                    : "text-gray-300 hover:bg-white/5 hover:text-gray-100"
                }`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066z" />
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </Link>
            </>
          )}
        </nav>
      </aside>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Layout                                                             */
/* ------------------------------------------------------------------ */

export function Layout({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  const { pathname } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Visible desktop links (filtered by auth)
  const desktopLinks = NAV_LINKS.filter(
    (l) => !l.authOnly || isAuthenticated,
  );

  // Visible mobile bottom tabs (filtered by auth)
  const mobileTabs = MOBILE_TABS.filter(
    (l) => !l.authOnly || isAuthenticated,
  );

  return (
    <div className="min-h-screen flex flex-col bg-surface text-gray-100">
      {/* ---- Top navbar ---- */}
      <header className="sticky top-0 z-30 bg-surface-card/95 backdrop-blur-md border-b border-white/10">
        <div className="mx-auto max-w-7xl flex items-center justify-between px-4 h-14">
          {/* Left: logo + hamburger */}
          <div className="flex items-center gap-3">
            {/* Hamburger (mobile only) */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-1.5 -ml-1.5 rounded-lg hover:bg-white/10 transition-colors text-gray-300"
              aria-label="Open navigation menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <Link
              to="/"
              className="text-lg font-bold text-brand-yellow hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              PokeDex Collector
            </Link>
          </div>

          {/* Center: desktop nav links */}
          <nav className="hidden md:flex items-center gap-1">
            {desktopLinks.map((link) => {
              const active = isActive(pathname, link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    active
                      ? "text-brand-yellow"
                      : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
                  }`}
                >
                  {link.label}
                  {/* Active underline indicator */}
                  <span
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full bg-brand-yellow transition-all duration-200 ${
                      active ? "w-4/5" : "w-0"
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          {/* Right: auth area */}
          <div className="flex items-center gap-2">
            {loading ? (
              <div className="h-8 w-8 rounded-full bg-white/5 animate-pulse" />
            ) : isAuthenticated ? (
              <>
                <NotificationBell />
                <UserMenu />
              </>
            ) : (
              <Link
                to="/login"
                className="text-sm font-semibold px-4 py-1.5 rounded-lg bg-brand-yellow text-surface hover:bg-brand-gold transition-colors"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* ---- Mobile sidebar drawer ---- */}
      <MobileSidebar
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        isAuthenticated={isAuthenticated}
        pathname={pathname}
      />

      {/* ---- Main content ---- */}
      <main className="flex-1 mx-auto w-full max-w-7xl px-4 py-6 pb-24 md:pb-6">
        {children}
      </main>

      {/* ---- Mobile bottom tab bar ---- */}
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-surface-card/95 backdrop-blur-md border-t border-white/10 md:hidden">
        <div className="flex items-center justify-around h-14">
          {mobileTabs.map((tab) => {
            const active = isActive(pathname, tab.to);
            return (
              <Link
                key={tab.to}
                to={tab.to}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-xs font-medium transition-colors ${
                  active
                    ? "text-brand-yellow"
                    : "text-gray-500 hover:text-gray-300"
                }`}
              >
                {tab.icon && <NavIcon d={tab.icon} />}
                <span>{tab.label}</span>
                {/* Active dot */}
                <span
                  className={`absolute bottom-1.5 h-1 w-1 rounded-full bg-brand-yellow transition-opacity duration-200 ${
                    active ? "opacity-100" : "opacity-0"
                  }`}
                />
              </Link>
            );
          })}
        </div>
      </nav>

      <CookieConsent />
    </div>
  );
}
